/**
 * Size system switching (EU / US / UK) for variant picker.
 * Swaps displayed text on buttons without affecting radio values.
 * Persists active system across variant morph re-renders.
 */

const SIZE_MAP = {
  33:   { us: '3',    uk: '1' },
  33.5: { us: '3.5',  uk: '1.5' },
  34:   { us: '4',    uk: '1.5' },
  34.5: { us: '4.5',  uk: '2' },
  35:   { us: '5',    uk: '2.5' },
  35.5: { us: '5.5',  uk: '3' },
  36:   { us: '5.5',  uk: '3.5' },
  36.5: { us: '6',    uk: '3.5' },
  37:   { us: '6',    uk: '4' },
  37.5: { us: '6.5',  uk: '4.5' },
  38:   { us: '7',    uk: '5' },
  38.5: { us: '7.5',  uk: '5.5' },
  39:   { us: '7.5',  uk: '6' },
  39.5: { us: '8',    uk: '6.5' },
  40:   { us: '8.5',  uk: '6.5' },
  40.5: { us: '9',    uk: '7' },
  41:   { us: '9.5',  uk: '7.5' },
  41.5: { us: '10',   uk: '8' },
  42:   { us: '10.5', uk: '8.5' },
  42.5: { us: '11',   uk: '9' },
  43:   { us: '11.5', uk: '9.5' },
  43.5: { us: '12',   uk: '10' },
  44:   { us: '12',   uk: '10' },
  44.5: { us: '12.5', uk: '10.5' },
  45:   { us: '13',   uk: '11' },
  45.5: { us: '13.5', uk: '11.5' },
  46:   { us: '14',   uk: '12' },
  46.5: { us: '14.5', uk: '12.5' },
  47:   { us: '15',   uk: '13' },
  47.5: { us: '15.5', uk: '13.5' },
  48:   { us: '16',   uk: '14' },
  48.5: { us: '16.5', uk: '14.5' },
  49:   { us: '17',   uk: '15' },
  50:   { us: '18',   uk: '16' },
};

/** Currently active size system — persists across morph */
let activeSystem = 'eu';

/**
 * Get the converted size value for the currently checked radio in this fieldset.
 */
function getCheckedConvertedValue(fieldset, system) {
  const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
  if (!checkedInput) return null;
  const label = checkedInput.closest('.variant-option__button-label');
  if (!label) return null;
  const textSpan = label.querySelector('.variant-option__button-label__text[data-eu-value]');
  if (!textSpan) return null;
  const euVal = textSpan.dataset.euValue;
  if (system === 'eu') return euVal;
  const converted = SIZE_MAP[parseFloat(euVal)];
  return converted ? converted[system] : euVal;
}

/**
 * Inject or update a hidden line item property in the product form.
 */
function updateLineItemProperty(fieldset, system) {
  const section = fieldset.closest('[id*="ProductInformation-"], [id*="QuickAdd-"]');
  if (!section) return;
  const form = section.querySelector('form[data-type="add-to-cart-form"]');
  if (!form) return;

  // Remove any previous size system property
  const prevInput = form.querySelector('input[data-size-system-prop]');
  if (prevInput) prevInput.remove();

  if (system === 'eu') return; // EU is default, no property needed

  const value = getCheckedConvertedValue(fieldset, system);
  if (!value) return;

  const systemsEl = fieldset.querySelector('[data-size-systems]');
  const label = systemsEl?.dataset.sizeLabel || 'Size';
  const systemLabel = system.toUpperCase();
  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = `properties[${label}]`;
  input.value = `${systemLabel}: ${value}`;
  input.dataset.sizeSystemProp = 'true';
  form.appendChild(input);
}

function applySystem(fieldset, system) {
  const textSpans = fieldset.querySelectorAll('.variant-option__button-label__text[data-eu-value]');
  textSpans.forEach((span) => {
    const euVal = span.dataset.euValue;
    if (system === 'eu') {
      span.textContent = euVal;
    } else {
      const converted = SIZE_MAP[parseFloat(euVal)];
      span.textContent = converted ? converted[system] : euVal;
    }
  });

  const buttons = fieldset.querySelectorAll('[data-size-system]');
  buttons.forEach((b) => {
    b.classList.toggle('variant-option__legend-system--active', b.dataset.sizeSystem === system);
  });

  updateLineItemProperty(fieldset, system);
}

function initSizeSystemSwitcher() {
  document.querySelectorAll('[data-size-systems]').forEach((container) => {
    const fieldset = container.closest('fieldset.variant-option--buttons');
    if (!fieldset) return;

    // Restore persisted system after morph
    if (activeSystem !== 'eu') {
      applySystem(fieldset, activeSystem);
    }

    const buttons = container.querySelectorAll('[data-size-system]');
    buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        activeSystem = btn.dataset.sizeSystem;
        applySystem(fieldset, activeSystem);
      });
    });
  });
}

// Init on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSizeSystemSwitcher);
} else {
  initSizeSystemSwitcher();
}

// Re-apply after variant morph re-renders the section
document.addEventListener('variant:update', () => {
  requestAnimationFrame(initSizeSystemSwitcher);
});
